import { synthesizeProceduralBuffer } from "./ProceduralSynth.ts";
import {
  MUSIC_LAYERS,
  MUSIC_VOICE_IDS,
  VOICE_DEFS,
  SHEDDABLE_LAYERS,
  GOVERNOR_SHED_LEVEL,
  DEFAULT_ENERGY,
  STEPS_PER_BAR,
  layerGainForEnergy,
  generateBarPattern,
  secondsPerStep,
  clampTempoScale,
  type MusicLayer,
  type MusicVoiceId,
  type MusicNoteEvent,
} from "./MusicPatterns.ts";

/**
 * ProceduralMusic.ts — Task-001 (2026-ingame-music-rework, AC-3/AC-6). The
 * `"run"` track's runtime: a lookahead step-scheduler playing short
 * pure-math-synthesized voice buffers (`MusicPatterns.ts`'s `VOICE_DEFS`,
 * reusing the exact `synthesizeProceduralSamples` recipe every SFX already
 * uses) through per-layer `GainNode`s into a caller-supplied `destination`
 * (`AudioEngine`'s `composerOut → musicFilter`, tech-analysis §2.1) — every
 * shipped music-reactive behavior on that chain (brightness/width/duck)
 * applies to the composer for free, by construction.
 *
 * The composer subscribes to NOTHING (no EventBus, no sim state, no
 * `gs.rng`) — it is driven exclusively through its small public API
 * (`start`/`stop`/`setEnergy`/`setTempoScale`), called by `AudioEngine`
 * (this task) and — in a later task — `MusicDirector`. Determinism lives in
 * `MusicPatterns.ts`'s pure functions; this class is the (stateful,
 * Web-Audio-touching) scheduler shell around them.
 */

/** Scheduler tick period — the same timer class as the existing heartbeat/
 *  ambience-loop intervals (`AudioEngine.ts`), cancelled on `destroy()` (the
 *  AA-19 lesson). Zero per-frame work — this is the ONLY place the composer
 *  does anything. */
const TICK_MS = 120;
/** How far ahead (seconds, on the AudioContext clock) each tick schedules
 *  while the document is VISIBLE — 2.5× the 120 ms tick period, comfortably
 *  over the ≥1.5× margin a lookahead scheduler needs so a single slow/
 *  delayed tick can never drop a note. */
const LOOKAHEAD_SEC = 0.3;
/** A-1 (perf-review finding, Task-001 attempt 2): the lookahead used while
 *  the document is HIDDEN but audio is still audible. Chromium/WebKit clamp
 *  a hidden tab's `setInterval` to ~1000 ms regardless of the requested
 *  120 ms (`TICK_MS`) — the real throttle margin that needs covering is
 *  THAT ~1000 ms clamp, not the 120 ms tick period a visible tab actually
 *  gets. `LOOKAHEAD_SEC`'s 0.3 s is ~2.5x the tick but only ~0.3x the
 *  throttle clamp, so a hidden/backgrounded tab would silently gap ~700 ms
 *  of audible music per tick. 1.6 s clears the required ≥1.5x margin
 *  against the ~1000 ms clamp (1.6 / 1.0 = 1.6). `tick()`'s existing
 *  forward-snap (`nextStepTime` never falls behind `ctx.currentTime`) still
 *  prevents a catch-up burst of stale notes when the tab regains
 *  visibility — this constant only widens how far a single throttled tick
 *  schedules, it does not change the catch-up behavior. */
const HIDDEN_LOOKAHEAD_SEC = 1.6;

export interface ProceduralMusicOptions {
  /** Constructor-seeded, render-side-only PRNG seed (mulberry32-style, via
   *  `MusicPatterns.ts`'s pure pattern generator) — defaults to `Date.now()`;
   *  injectable so tests pin exact pattern output (tech-analysis §2.3). */
  seed?: number;
  /** Pull-based PerfGovernor level getter (B-6 — no governor API change);
   *  polled once per scheduler tick, never per-note. Omitted → never sheds. */
  getLoadLevel?: () => number;
  /** Optional diagnostics sink mirroring `AudioEngine`'s trace ring buffer
   *  (`proc.start`/`proc.stop`/`proc.shed`) — cheap, cold-path only. */
  trace?: (kind: string, detail?: string) => void;
}

export class ProceduralMusic {
  private readonly ctx: AudioContext;
  private readonly seed: number;
  private readonly getLoadLevel: (() => number) | null;
  private readonly traceFn: ((kind: string, detail?: string) => void) | null;

  private readonly layerGains: Record<MusicLayer, GainNode>;
  private readonly voiceBuffers: Record<MusicVoiceId, AudioBuffer>;

  private playing_ = false;
  private timer: ReturnType<typeof setInterval> | null = null;
  private nextStepTime = 0;
  private currentStep = 0;
  private currentBar = 0;
  private barEvents: readonly MusicNoteEvent[] = [];
  private shedActive_ = false;
  private readonly activeSources = new Set<AudioBufferSourceNode>();

  private energy_ = DEFAULT_ENERGY;
  // A lightweight manual ramp for the (non-AudioParam) scheduler-side tempo
  // scale — mirrors the cancel+anchor+linearRamp shape every other music
  // tone primitive uses (setMusicBrightness/setMusicWidth/setMusicDuck),
  // just computed on the JS side since it drives step-timing math, not a
  // real AudioParam.
  private tempoFrom_ = 1;
  private tempoTo_ = 1;
  private tempoRampStartSec_ = 0;
  private tempoRampEndSec_ = 0;

  constructor(
    ctx: AudioContext,
    destination: AudioNode,
    opts?: ProceduralMusicOptions,
  ) {
    this.ctx = ctx;
    this.seed = opts?.seed ?? Date.now();
    this.getLoadLevel = opts?.getLoadLevel ?? null;
    this.traceFn = opts?.trace ?? null;

    this.layerGains = {} as Record<MusicLayer, GainNode>;
    for (const layer of MUSIC_LAYERS) {
      const node = ctx.createGain();
      node.gain.value = layerGainForEnergy(layer, this.energy_);
      node.connect(destination);
      this.layerGains[layer] = node;
    }

    // Voices pre-synthesized ONCE at init (D-298: a handful of buffers, not
    // one per note) via the shared pure-math synthesis machinery — see
    // `ProceduralSynth.ts`'s doc for why this is the same code path every
    // SFX profile renders through. F-1 (perf-review, Task-001 attempt 2):
    // measured at a 44.1 kHz ctx rate, the two LONG voices (pad ~6.24 s,
    // tension 1.5 s) alone retained 1.302 MiB (1,365,336 B) of a ~1.40 MiB six-buffer
    // total — ~30x this comment's original ("dozens of KB") claim, inside
    // the WKWebView-jetsam-sensitive boot window (`main.ts:1119`). Fixed by
    // rendering those two at `LONG_VOICE_RENDER_SAMPLE_RATE_HZ` (11,025 Hz —
    // both are pure low sines, audibly transparent there; see its doc in
    // `MusicPatterns.ts`) via `renderSampleRateHz`, cutting them to ~269 KiB
    // (pad) + ~65 KiB (tension). Measured post-fix total across all six
    // buffers: ~431 KiB (mono Float32Array — 4 bytes/sample; see the
    // Coder's report for the per-voice `buffer.length x 4` breakdown) —
    // stated here honestly, not re-claimed as "dozens of KB".
    //
    // Task-003 (2026-audio-polish, AC-6/AC-7) — a SEVENTH voice, `lead` (the
    // melody), joins at the same `LONG_VOICE_RENDER_SAMPLE_RATE_HZ` (it too
    // is pure low sine content, ≤ ~800 Hz — see `leadMaxFreqHz()`):
    // 800 ms × 11,025 Hz × 4 B = 35,280 B (34.45 KiB) exactly. New measured
    // seven-buffer total: 440,996 B + 35,280 B = 476,276 B (465.1 KiB) — the
    // D-298-honest "≈ +35 KiB on 430.7 KiB → ≈ 466 KiB" the spec/tech-analysis
    // estimated, confirmed here to the byte (still far under the pre-F-1
    // 1.4 MiB).
    this.voiceBuffers = {} as Record<MusicVoiceId, AudioBuffer>;
    for (const voiceId of MUSIC_VOICE_IDS) {
      const def = VOICE_DEFS[voiceId];
      this.voiceBuffers[voiceId] = synthesizeProceduralBuffer(
        ctx,
        def.recipe,
        def.renderSampleRateHz,
      );
    }
  }

  isPlaying(): boolean {
    return this.playing_;
  }

  /** True while PerfGovernor load has shed `arp`/`percussion` (test/debug
   *  visibility for the shed witness). */
  get shedActive(): boolean {
    return this.shedActive_;
  }

  /**
   * (Re)start the scheduler from bar 0. Idempotent-safe: if already playing,
   * stops first (matches `AudioEngine.startMusicSource`'s own semantics —
   * callers already hard-stop whatever was live before starting a track).
   * The timer idles harmlessly while `ctx.state !== "running"` (see `tick`).
   */
  start(): void {
    if (this.playing_) this.stop();
    this.playing_ = true;
    this.currentBar = 0;
    this.currentStep = 0;
    this.shedActive_ = false;
    this.nextStepTime = this.ctx.currentTime;
    this.barEvents = generateBarPattern(this.seed, this.energy_, 0);
    this.timer = setInterval(() => this.tick(), TICK_MS);
    this.traceFn?.("proc.start");
  }

  /** Halt scheduling + stop any currently-sounding voices. Safe to call when
   *  already stopped (idempotent — no duplicate trace entry). Restartable
   *  via `start()`. */
  stop(): void {
    if (this.timer !== null) {
      clearInterval(this.timer);
      this.timer = null;
    }
    const wasPlaying = this.playing_;
    this.playing_ = false;
    for (const source of this.activeSources) {
      try {
        source.stop();
      } catch {
        /* already stopped */
      }
    }
    this.activeSources.clear();
    if (wasPlaying) this.traceFn?.("proc.stop");
  }

  /** Final teardown (AA-19: the scheduler timer is always cleared here even
   *  if `stop()` was never called) — releases the per-layer graph nodes too.
   *  Not restartable after this. */
  destroy(): void {
    this.stop();
    for (const layer of MUSIC_LAYERS) {
      try {
        this.layerGains[layer].disconnect();
      } catch {
        /* already disconnected */
      }
    }
  }

  /**
   * The ONE energy input (0..1) — smoothly re-levels every layer's GainNode
   * to `layerGainForEnergy(layer, energy)` (tech-analysis §2.4). Safe to call
   * before `start()` (sets the initial mix the next `start()` snaps to) or
   * while playing (a ramped re-mix, no click).
   */
  setEnergy(energy: number, fadeMs = 400): void {
    const e = Math.max(0, Math.min(1, energy));
    this.energy_ = e;
    const now = this.ctx.currentTime;
    for (const layer of MUSIC_LAYERS) {
      const node = this.layerGains[layer];
      const target = layerGainForEnergy(layer, e);
      node.gain.cancelScheduledValues(now);
      node.gain.setValueAtTime(node.gain.value, now);
      node.gain.linearRampToValueAtTime(
        target,
        now + Math.max(0, fadeMs) / 1000,
      );
    }
  }

  /** Tempo scale, clamped ±6% (mirrors `AudioEngine.setMusicRate` exactly) —
   *  scales BOTH the scheduler's step duration and each note's
   *  `playbackRate` (a coherent, "tape-speed" tempo change; tech-analysis
   *  §2.5). Ramped smoothly like every other music tone primitive. */
  setTempoScale(scale: number, fadeMs = 300): void {
    const s = clampTempoScale(scale);
    const now = this.ctx.currentTime;
    this.tempoFrom_ = this.tempoScaleAt(now);
    this.tempoTo_ = s;
    this.tempoRampStartSec_ = now;
    this.tempoRampEndSec_ = now + Math.max(0, fadeMs) / 1000;
  }

  private tempoScaleAt(timeSec: number): number {
    if (timeSec >= this.tempoRampEndSec_) return this.tempoTo_;
    const span = this.tempoRampEndSec_ - this.tempoRampStartSec_;
    if (span <= 0) return this.tempoTo_;
    const t = Math.max(
      0,
      Math.min(1, (timeSec - this.tempoRampStartSec_) / span),
    );
    return this.tempoFrom_ + (this.tempoTo_ - this.tempoFrom_) * t;
  }

  /**
   * The scheduler tick (~120 ms, `TICK_MS`). Idles — does no scheduling work
   * at all — while `ctx.state !== "running"` (backgrounding/interruption;
   * the deferral machinery re-asserts the track on resume). Never lets
   * `nextStepTime` fall behind the clock across a long stall, so resuming
   * never dumps a burst of catch-up notes. Pulls `getLoadLevel()` ONCE per
   * tick (not per note) for the governor shed. Schedules `LOOKAHEAD_SEC`
   * ahead while the document is visible, `HIDDEN_LOOKAHEAD_SEC` while it is
   * hidden (A-1 — see that constant's doc for why the hidden case needs a
   * much wider horizon than the tick period alone suggests); the `typeof`
   * guard keeps this a safe no-op in the node/vitest test environment, which
   * has no global `document`.
   */
  private tick(): void {
    if (!this.playing_) return;
    if (this.ctx.state !== "running") return;
    if (this.nextStepTime < this.ctx.currentTime) {
      this.nextStepTime = this.ctx.currentTime;
    }
    const level = this.getLoadLevel?.() ?? 0;
    const shed = level >= GOVERNOR_SHED_LEVEL;
    if (shed !== this.shedActive_) {
      this.shedActive_ = shed;
      this.traceFn?.(
        "proc.shed",
        shed ? `on level=${level}` : `off level=${level}`,
      );
    }
    const lookahead =
      typeof document !== "undefined" && document.visibilityState === "hidden"
        ? HIDDEN_LOOKAHEAD_SEC
        : LOOKAHEAD_SEC;
    const horizon = this.ctx.currentTime + lookahead;
    while (this.nextStepTime < horizon) {
      this.scheduleStep(this.nextStepTime, shed);
      this.advanceStep();
    }
  }

  private scheduleStep(time: number, shed: boolean): void {
    if (this.currentStep === 0) {
      this.barEvents = generateBarPattern(
        this.seed,
        this.energy_,
        this.currentBar,
      );
    }
    for (const ev of this.barEvents) {
      if (ev.stepIndex !== this.currentStep) continue;
      if (shed && SHEDDABLE_LAYERS.has(ev.layer)) continue;
      this.playNote(ev, time);
    }
  }

  private advanceStep(): void {
    // T-1 (perf-review finding, Task-001 attempt 2): DIVIDE, not multiply.
    // `playbackRate` below is `pitchRatio * tempo` — at tempo > 1 a note
    // already plays faster/higher (tape sped up). The step grid must speed
    // up by the SAME factor for the two to stay coherent, which means the
    // wall-clock gap between steps must SHRINK at tempo > 1 — i.e. divide.
    // Multiplying (the bug) made the grid slower exactly when every note on
    // it was playing faster, inverted vs the buffer path's own tape-speed
    // semantics.
    this.nextStepTime += secondsPerStep() / this.tempoScaleAt(this.nextStepTime);
    this.currentStep++;
    if (this.currentStep >= STEPS_PER_BAR) {
      this.currentStep = 0;
      this.currentBar++;
    }
  }

  private playNote(ev: MusicNoteEvent, time: number): void {
    const buffer = this.voiceBuffers[ev.voice];
    const def = VOICE_DEFS[ev.voice];
    const tempo = this.tempoScaleAt(time);
    const pitchRatio =
      def.pitched && def.referenceFreqHz > 0
        ? ev.freqHz / def.referenceFreqHz
        : 1;

    const source = this.ctx.createBufferSource();
    source.buffer = buffer;
    source.playbackRate.value = pitchRatio * tempo;

    const noteGain = this.ctx.createGain();
    noteGain.gain.value = ev.velocity;
    source.connect(noteGain);
    noteGain.connect(this.layerGains[ev.layer]);

    this.activeSources.add(source);
    source.onended = () => {
      this.activeSources.delete(source);
      try {
        source.disconnect();
        noteGain.disconnect();
      } catch {
        /* already disconnected */
      }
    };
    source.start(time);
  }
}
